import { Box, styled, type SxProps, type Theme } from '@mui/material';
import { useEffect, useState } from 'react';

import { CenteredLoader } from '../CenteredLoader/CenteredLoader';
import { Image as UiImage, type Props as ImageProps } from '../Image/Image';

type Props = ImageProps & {
  placeholderSrc?: string;
  src: string;
  showLoader?: boolean;
  loaderSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  zoomOnHover?: boolean;
  minHeight?: string | number;
};

const Container = styled(Box, {
  shouldForwardProp: (prop) => !['rounded', 'minHeight'].includes(prop as string),
})<Pick<Props, 'rounded' | 'minHeight'>>(({ rounded, theme, minHeight }) => ({
  borderRadius: rounded ? theme.shape.borderRadius * 2 : 0,
  display: 'flex',
  height: 'inherit',
  minHeight,
  overflow: 'hidden',
  width: 'inherit',
}));

const LoaderContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
});

export const ProgressiveImage = ({
  placeholderSrc,
  src,
  sx = {},
  showLoader = true,
  loaderSx = {},
  zoomOnHover = false,
  minHeight = 'auto',
  ...props
}: Props) => {
  const [imgSrc, setImgSrc] = useState(placeholderSrc || src);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setImgSrc(placeholderSrc || src);

    const img = new Image();
    img.src = src;

    const handleLoad = () => {
      setImgSrc(src);
      setIsLoading(false);
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener('load', handleLoad);
    }

    return () => {
      img.removeEventListener('load', handleLoad);
    };
  }, [src, placeholderSrc]);

  return (
    <Container
      rounded={props.rounded}
      minHeight={minHeight}
      sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', position: 'relative' }}
    >
      {showLoader && isLoading ? (
        <LoaderContainer>
          <CenteredLoader sx={loaderSx} />
        </LoaderContainer>
      ) : null}
      <UiImage
        {...props}
        src={imgSrc}
        sx={{
          '&:hover': {
            transform: zoomOnHover ? 'scale(1.2)' : 'none',
          },
          ...(placeholderSrc && imgSrc === placeholderSrc
            ? {
                clipPath: 'inset(0)',
                filter: 'blur(10px)',
                opacity: 0.5,
                ...sx,
              }
            : {
                filter: 'none',
                opacity: isLoading ? 0 : 1,
                transition: (theme: Theme) =>
                  theme.transitions.create(['filter', 'opacity', 'transform'], {
                    duration: theme.transitions.duration.complex,
                    easing: theme.transitions.easing.easeInOut,
                  }),
                ...sx,
              }),
        }}
      />
    </Container>
  );
};
